import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import {
  AppNotificationToast,
  AppNotificationToastActionButton,
} from '../AppNotificationToast';
import { resolveUpdateUiPhase } from './updateStatusUtils';

function resolveUpdateErrorMessage(status, p) {
  const msg = (status?.message || '').trim();
  if (
    msg === 'انتهت مهلة التحقق من التحديث' ||
    msg === 'استجابة غير صالحة من الخادم' ||
    msg === 'Update check timed out' ||
    msg === 'Invalid JSON response'
  ) {
    return p('check_error');
  }
  if (msg && /[\u0600-\u06FF]/.test(msg)) return msg;
  return p('toast_failed');
}

function resolveToastConfig(status, t) {
  const p = (key, opts) => t(`modules.shared.settingsBody.app_update.${key}`, opts);
  const uiPhase = resolveUpdateUiPhase(status);
  if (!uiPhase || uiPhase === 'idle' || uiPhase === 'checking' || status?.phase === 'dismissed') {
    return null;
  }

  const newVersion = status.version || '…';
  const changelog = (status.changelog || '').trim() || p('changelog_default');
  const progress = status.downloadProgress || 0;

  if (uiPhase === 'available') {
    return {
      phase: uiPhase,
      variant: 'success',
      showActions: true,
      topLabel: p('label_update'),
      message: p('toast_new_version', { version: newVersion, notes: changelog }),
      version: newVersion,
      mandatory: Boolean(status.mandatory),
      placement: 'bottom',
    };
  }

  if (uiPhase === 'ready') {
    return {
      phase: uiPhase,
      variant: 'success',
      showActions: true,
      topLabel: p('label_update'),
      message: p('toast_ready', { version: newVersion }),
      version: newVersion,
      mandatory: Boolean(status.mandatory),
      placement: 'bottom',
    };
  }

  if (uiPhase === 'downloading') {
    return {
      phase: uiPhase,
      variant: 'progress',
      showActions: true,
      topLabel: p('label_wait'),
      message: p('downloading', { version: newVersion, progress }),
      version: newVersion,
      mandatory: Boolean(status.mandatory),
      downloadProgress: progress,
      placement: 'bottom',
    };
  }

  if (uiPhase === 'installing') {
    return {
      phase: uiPhase,
      variant: 'progress',
      showActions: true,
      topLabel: p('label_wait'),
      message: p('toast_installing', { version: newVersion }),
      version: newVersion,
      mandatory: Boolean(status.mandatory),
      placement: 'bottom',
    };
  }

  if (uiPhase === 'error') {
    return {
      phase: uiPhase,
      variant: 'error',
      showActions: false,
      topLabel: p('label_error'),
      message: resolveUpdateErrorMessage(status, p),
      version: newVersion,
      mandatory: Boolean(status.mandatory),
      placement: 'bottom',
    };
  }

  return null;
}

const TOAST_PHASES = new Set(['available', 'ready', 'downloading', 'installing', 'error']);
const DOWNLOAD_UI_THROTTLE_MS = 1200;

export default function UpdateNotificationBar() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.settingsBody.app_update.${key}`, opts);
  const [status, setStatus] = useState(null);
  const [softHidden, setSoftHidden] = useState(false);
  const dismissedVersionRef = useRef(null);
  const lastStatusKeyRef = useRef('');
  const lastDownloadUiAtRef = useRef(0);
  const lastDownloadPctRef = useRef(-1);

  useEffect(() => {
    const api = window.electronAPI?.updates;
    if (!api) return undefined;

    const applyStatus = (payload) => {
      if (!payload || typeof payload !== 'object') return;

      const uiPhase = resolveUpdateUiPhase(payload);
      if (!TOAST_PHASES.has(uiPhase)) return;

      if (uiPhase === 'downloading') {
        const now = Date.now();
        const pct = payload.downloadProgress || 0;
        if (
          pct < 100 &&
          now - lastDownloadUiAtRef.current < DOWNLOAD_UI_THROTTLE_MS &&
          pct === lastDownloadPctRef.current
        ) {
          return;
        }
        lastDownloadUiAtRef.current = now;
        lastDownloadPctRef.current = pct;
      }

      const key = `${uiPhase}|${payload.downloadProgress || 0}|${payload.version || ''}|${payload.message || ''}`;
      if (key === lastStatusKeyRef.current) return;
      lastStatusKeyRef.current = key;
      setStatus(payload);

      if (payload.phase === 'downloading' || payload.phase === 'installing') {
        setSoftHidden(false);
        return;
      }
      if (
        (payload.phase === 'available' || payload.phase === 'ready') &&
        payload.version &&
        payload.version !== dismissedVersionRef.current
      ) {
        setSoftHidden(false);
      }
    };

    let cancelled = false;
    let unsubscribe = () => {};

    const start = () => {
      if (cancelled) return;
      unsubscribe = api.onStatus(applyStatus) || (() => {});
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(start, { timeout: 8000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
        unsubscribe();
      };
    }

    const timer = window.setTimeout(start, 3000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const toast = useMemo(() => resolveToastConfig(status, t), [status, t]);

  if (!window.electronAPI?.isElectron || softHidden || !toast) return null;

  const handleDownload = () => window.electronAPI?.updates?.download();
  const handleInstall = () => window.electronAPI?.updates?.install();
  const handleSoftDismiss = () => {
    dismissedVersionRef.current = toast.version;
    setSoftHidden(true);
  };

  const canDismiss =
    !toast.mandatory && toast.phase !== 'installing' && toast.phase !== 'downloading';

  const actionFooter =
    toast.phase === 'available' ? (
      <AppNotificationToastActionButton onClick={handleDownload}>
        <Download className="h-4 w-4" />
        {p('download')}
      </AppNotificationToastActionButton>
    ) : toast.phase === 'ready' ? (
      <AppNotificationToastActionButton onClick={handleInstall}>
        <Download className="h-4 w-4" />
        {p('install')}
      </AppNotificationToastActionButton>
    ) : toast.phase === 'downloading' ? (
      <div className="w-full space-y-2">
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-200"
            style={{ width: `${toast.downloadProgress || 0}%` }}
          />
        </div>
      </div>
    ) : toast.phase === 'installing' ? (
      <div className="flex items-center gap-2 text-sm text-white/70">
        <Loader2 className="h-4 w-4 animate-spin" />
        {p('preparing')}
      </div>
    ) : null;

  return (
    <AppNotificationToast
      visible
      variant={toast.variant}
      showActions={toast.showActions}
      topLabel={toast.topLabel}
      message={toast.message}
      onDismiss={handleSoftDismiss}
      showDismiss={canDismiss}
      dismissOnBackdrop={false}
      placement={toast.placement || 'bottom'}
    >
      {actionFooter}
    </AppNotificationToast>
  );
}
